import React, {useEffect, useState} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Keyboard, Platform, StatusBar} from 'react-native';
import TextGeneric from '../../../components/textGeneric';
import GenericButton from '../../../components/button';
import {Theme} from '../../../theme/lightTheme';
import MainLayout from '../../mainLayout';
import Animated, {FadeIn, FadeOut} from 'react-native-reanimated';
import {tr} from '../../../translations';
import TextInput from '../../../components/textInput';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {forgetPassword} from './actions';
import {ScreenProps} from '../../../interfaces/screens/mainScreen';
import BackIcon from '../../../components/header/backIcon';
import screens from '../../../config/screens';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';

const Container = styled.View<{height: number; paddingTop: number}>`
  width: 100%;
  height: ${({height}) => height}px;
  padding-top: ${({paddingTop}) => paddingTop}px;
`;

const ContentContainer = styled.View`
  width: 100%;
  flex: 1;
  align-items: center;
  justify-content: space-between;
`;

const ImageContainer = styled(Animated.View)<{width: number}>`
  width: ${({width}) => width}px;
  aspect-ratio: 1.333;
  align-items: center;
  justify-content: center;
  align-self: center;
  overflow: hidden;
`;

const Image = styled.Image`
  width: 100%;
  height: 100%;
  ${Platform.OS !== 'web' && 'resize-mode: contain;'}
`;

const BackIconContainer = styled.View<{topPosition: number}>`
  position: absolute;
  top: ${({topPosition}) => topPosition}px;
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  z-index: 99;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
`;

const Title = styled(TextGeneric)<{
  color: string;
  fontSize: number;
  paddingBottom: number;
}>`
  color: ${({color}) => color};
  font-size: ${({fontSize}) => fontSize}px;
  text-align: center;
  padding-bottom: ${({paddingBottom}) => paddingBottom}px;
`;

const SubTitle = styled(TextGeneric)<{fontSize: number; lineHeight: number}>`
  font-size: ${({fontSize}) => fontSize}px;
  color: ${({theme}) => theme.text.title};
  text-align: left;
  line-height: ${({lineHeight}) => lineHeight}px;
  margin-bottom: 5%;
  padding-top: 15%;
`;

const SubmitButton = styled(GenericButton)<{
  width: number;
  backgroundColor: string;
}>`
  width: ${({width}) => width}px;
  border-radius: 100px;
  background-color: ${({backgroundColor}) => backgroundColor};
`;

const ButtonContainer = styled.View<{width: number}>`
  flex: 1;
  width: ${({width}) => width}px;
  justify-content: flex-end;
`;

const ErrorText = styled(TextGeneric)<{
  color: string;
  fontSize: number;
  paddingTop: number;
}>`
  color: ${({color}) => color};
  font-size: ${({fontSize}) => fontSize}px;
  padding-top: ${({paddingTop}) => paddingTop}px;
  text-align: center;
`;

const ErrorContainer = styled.View<{minHeight: number}>`
  min-height: ${({minHeight}) => minHeight}px;
`;

const ForgetPassword = ({navigation}: ScreenProps) => {
  const theme = useTheme() as Theme;
  const {top: topInset, bottom: bottomInset} = useSafeAreaInsets();
  const [isErrorEmail, setIsErrorEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  // Use the responsive dimensions hook
  const {width, height, hdp, wdp, getByScreenSize, isLargeScreen} =
    useResponsiveDimensions();

  const isValidEmail = (val: string) => {
    return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(val);
  };

  useEffect(() => {
    setIsErrorEmail(!isValidEmail(email));
  }, [email]);

  // Custom back handler for web direct navigation
  const handleBackNavigation = () => {
    if (Platform.OS === 'web') {
      // Always navigate to login page on web
      navigation?.navigate(screens.auth.login);
    } else if (navigation?.goBack) {
      navigation.goBack();
    }
  };

  const forgetPasswordHandler = async () => {
    setError('');
    Keyboard.dismiss();
    const res = await forgetPassword((email || '').toLowerCase().trim());
    if (!res?.success && res?.message) {
      setError(res?.message);
    } else {
      // Navigate to reset password screen with email
      navigation?.navigate(screens.auth.resetPassword, {
        email: (email || '').toLowerCase().trim(),
      });
    }
  };

  // Remove the existing dimensions and getResponsive functions
  const containerHeight =
    height -
    ((StatusBar.currentHeight || 0) + Math.max(bottomInset, height * 0.02));

  // Calculate responsive sizes
  const imageWidth = wdp(getByScreenSize(60, 15, 40));
  const inputWidth = isLargeScreen ? '40%' : '100%';
  const buttonContainerWidth = wdp(getByScreenSize(90, 20, 90));
  const submitButtonWidth = wdp(getByScreenSize(90, 20));

  // Fix for web where topInset can be 0
  const backIconTopPosition = Math.max(10, topInset + theme.text.s6);

  return (
    <MainLayout
      isCustomHeader
      showOverlay={false}
      noPaddingHeader
      keyboardAvoidScrollView>
      <Container
        height={containerHeight}
        paddingTop={Math.max(topInset, hdp(5))}>
        <Title
          color={theme.text.dark}
          fontSize={theme.text.s4}
          paddingBottom={hdp(5)}>
          {tr('screens.forgetPasswordPage')}
        </Title>
        <BackIconContainer topPosition={backIconTopPosition}>
          <BackIcon
            tintColor={theme.secondary}
            canGoBack
            onPress={handleBackNavigation}
            iconName="back"
          />
        </BackIconContainer>
        <ContentContainer>
          <ImageContainer entering={FadeIn} width={imageWidth}>
            <Image
              resizeMode={'contain'}
              source={require('../../../assets/images/lock.png')}
            />
          </ImageContainer>
          <Animated.View entering={FadeIn.duration(700)}>
            <SubTitle fontSize={theme.text.s8} lineHeight={theme.text.s9 * 1.4}>
              {tr('forgetPassword.subTitle')}
            </SubTitle>
          </Animated.View>
          <TextInput
            placeholder={tr('forgetPassword.email')}
            value={email}
            showLeftIcon
            leftIconName={'user'}
            leftIconType={'Feather'}
            leftIconColor={theme.text.dark}
            onChangeText={text => setEmail(text)}
            keyboardType={'email-address'}
            showRightIcon={email.length > 0 && !isErrorEmail}
            rightIconType={'AntDesign'}
            rightIconSize={theme.text.s6}
            rightIconName={'checkcircle'}
            containerStyle={{
              width: inputWidth,
            }}
          />
          <ErrorContainer minHeight={theme.text.s10 * 5}>
            {error && (
              <Animated.View entering={FadeIn} exiting={FadeOut}>
                <ErrorText
                  color={theme.text.error}
                  fontSize={theme.text.s9}
                  paddingTop={hdp(4)}>
                  {error || tr('login.error')}
                </ErrorText>
              </Animated.View>
            )}
          </ErrorContainer>
          <ButtonContainer width={buttonContainerWidth}>
            <SubmitButton
              label={tr('forgetPassword.send')}
              radius={100}
              width={submitButtonWidth}
              onPress={forgetPasswordHandler}
              disabled={isErrorEmail}
              backgroundColor={isErrorEmail ? theme.text.light : theme.primary}
              labelStyle={{
                color: isErrorEmail ? theme.secondary : theme.text.light,
              }}
            />
          </ButtonContainer>
        </ContentContainer>
      </Container>
    </MainLayout>
  );
};

export default ForgetPassword;
